import { fetchClinicStudiesSupport } from '@/entities/study';
import { handleSubmitSupportRequest } from '@/entities/support';
import { INVALID_EMAIL_ERROR_MESSAGE, LOADING_TYPES } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { showMessageError, showMessageSuccess, validateEmail } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';
import {
  buildSubmitPayload,
  isSubmitDisabled as computeIsSubmitDisabled,
  filterStudyIdInput,
  mapValidationResult,
  MAX_STUDY_ID,
} from '../../../lib/changeStudyInformation';
import type {
  ChangeStudyInformationProps,
  ChangeStudyInformationState,
} from './changeStudyInformation.types';

/**
 * Holds the form state of the change-study-information support request: one study id, its
 * validation result, reply emails and message. Validates the study id 300 ms after it reaches
 * five digits, resets the whole form when the facility changes, derives whether submit is
 * disabled, and submits the payload from buildSubmitPayload through handleSubmitSupportRequest.
 *
 * @param props - Form props: category, selectedFacility, setLoadingPage and backToPrevious.
 * @returns An object carrying the form state and setter, the study id change and validation
 *   handlers, the field, email and send handlers, and the submit-disabled flag.
 */
export const useChangeStudyInformation = ({
  category,
  selectedFacility,
  setLoadingPage,
  backToPrevious,
}: ChangeStudyInformationProps) => {
  const [state, setState] = useMergeState<ChangeStudyInformationState>({
    studyId: '',
    validatedStudy: null,
    studyIdError: '',
    isValidating: false,
    emailList: [],
    emailInputValue: '',
    message: '',
    errorObject: {},
    isSaving: false,
    isFormExpanded: false,
    lastValidatedStudy: '',
  });

  /**
   * Accepts an edit of the study id field in the change-study-information request. Normalises the typed text through filterStudyIdInput, ignores a non-clearing edit that leaves the id unchanged, and otherwise stores the new id while dropping the previously validated study and its error.
   *
   * @param name - Name of the changed field, unused because only the study id routes here.
   * @param value - Raw text typed into the study id input.
   * @returns Nothing.
   */
  const onStudyIdChange = (name: any, value: any) => {
    const nextValue = filterStudyIdInput(state.studyId, value);
    if (nextValue === state.studyId && value !== '') {
      return;
    }
    setState({
      studyId: nextValue,
      validatedStudy: null,
      studyIdError: '',
    });
  };

  const latestStudyIdRef = useRef('');

  /**
   * Checks the study id typed into the change-study-information request against the clinic studies of the selected facility. Skips already validated or empty ids, rejects ids above MAX_STUDY_ID outright, and otherwise queries fetchClinicStudiesSupport including RMA studies, ignoring the answer when a newer id was typed meanwhile, then stores the study from mapValidationResult or an open-request or invalid-id message.
   *
   * @returns A promise settling once the validated study or its error message is in state.
   */
  const validateStudyId = async () => {
    const { studyId } = state;
    if (!studyId || studyId === state.lastValidatedStudy) {
      return;
    }
    if (Number(studyId) > MAX_STUDY_ID) {
      setState({
        validatedStudy: null,
        studyIdError: t`Invalid study ID`,
        lastValidatedStudy: studyId,
        isValidating: false,
      });
      return;
    }
    latestStudyIdRef.current = studyId;
    setState({ isValidating: true });
    try {
      const result = await fetchClinicStudiesSupport(
        {
          studyFid: Number(studyId),
          includeRma: true,
          facilityId: selectedFacility?.value,
          viewAll: true,
        },
        1,
      );
      if (latestStudyIdRef.current !== studyId) {
        return;
      }
      const { study, error } = mapValidationResult(result);
      const errorMessage =
        error === 'OPEN_REQUEST'
          ? t`This study currently has an open request.`
          : t`Invalid study ID`;
      setState({
        validatedStudy: study,
        studyIdError: error ? errorMessage : '',
        isValidating: false,
        lastValidatedStudy: studyId,
        isFormExpanded: false,
      });
    } catch (error) {
      if (latestStudyIdRef.current !== studyId) {
        return;
      }
      console.error('Failed to validate study ID: ', error);
      setState({
        validatedStudy: null,
        studyIdError: t`Invalid study ID`,
        isValidating: false,
        lastValidatedStudy: studyId,
      });
    }
  };

  useEffect(() => {
    if (state.studyId.length < 5) {
      return undefined;
    }
    if (state.studyId === state.lastValidatedStudy) {
      return undefined;
    }

    const timer = setTimeout(validateStudyId, 300);
    return () => clearTimeout(timer);
  }, [state.studyId]);

  /**
   * Records one answer of the support request form and drops the error attached to that field. Deep-clones errorObject, deletes the entry keyed by the field name and stores both the value and the trimmed error map.
   *
   * @param name - Key of the support request field being answered.
   * @param value - Value entered for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject[name];
    setState({ [name]: value, errorObject: cloneErrorObject });
  };

  /**
   * Stores the reply-to email addresses collected by the support request form. Deep-clones errorObject without its invalidEmails entry, then saves the new list and empties the pending email input.
   *
   * @param value - Accepted email addresses of the reply-to list.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailList: value, emailInputValue: '', errorObject: cloneErrorObject });
  };

  /**
   * Tracks the half-typed reply-to address of the support request form. Deep-clones errorObject without its invalidEmails entry and stores the raw input text alongside it.
   *
   * @param value - Current text of the email input.
   * @returns Nothing.
   */
  const onInputChange = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailInputValue: value, errorObject: cloneErrorObject });
  };

  /**
   * Submits a change-study-information support request for the validated study. Rejects a half-typed reply-to address through validateEmail and returns with an invalidEmails error, otherwise shows the submitting loader, posts the payload built by buildSubmitPayload from the category, facility, validated study, message and email list, then confirms with a success toast and navigates back, or logs the error and reports a send failure.
   *
   * @returns A promise settling once the request was sent and the page navigated back, or the failure reported.
   */
  const onClickSendMessage = async () => {
    setState({ isSaving: true });
    const errorObject = {};
    const isValidCurrentInput = !state.emailInputValue || validateEmail(state.emailInputValue);
    if (!isValidCurrentInput) {
      _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
    }
    if (!_.isEmpty(errorObject)) {
      setState({ errorObject, isSaving: false });
      return;
    }
    try {
      setLoadingPage(LOADING_TYPES.SUBMITTING);
      await handleSubmitSupportRequest(
        buildSubmitPayload({
          category,
          selectedFacilityValue: selectedFacility?.value,
          validatedStudy: state.validatedStudy,
          message: state.message,
          emailList: state.emailList,
        }),
      );
      showMessageSuccess({
        content: t`We have received your request and will email you back when it has been completed`,
      });
      setLoadingPage(false);
      backToPrevious();
    } catch (error) {
      console.error('Failed to send support email: ', error);
      showMessageError({ content: t`Failed to send your message` });
      setState({ isSaving: false });
      setLoadingPage(false);
    }
  };

  const isSubmitDisabled = computeIsSubmitDisabled({
    validatedStudy: state.validatedStudy,
    message: state.message,
    emailList: state.emailList,
    emailInputValue: state.emailInputValue,
    isSaving: state.isSaving,
  });

  useEffect(() => {
    latestStudyIdRef.current = '';
    setState({
      studyId: '',
      validatedStudy: null,
      studyIdError: '',
      isValidating: false,
      lastValidatedStudy: '',
      emailList: [],
      emailInputValue: '',
      message: '',
      errorObject: {},
      isFormExpanded: false,
    });
  }, [selectedFacility]);

  return {
    state,
    setState,
    onStudyIdChange,
    validateStudyId,
    onChange,
    onChangeEmailList,
    onInputChange,
    onClickSendMessage,
    isSubmitDisabled,
  };
};
